import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getStaff, updateStaff } from "../../store/slices/staff";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import {
  nameregex,
  emailregex,
  mobileregex,
  expregex,
  educationRegex,
  aadharregex,
  religionnameregex,
  nationalitynameregex,
  isValidDateOfBirth,
} from "../../utils/Regex";
import { uploadadminImage } from "../../utils/helpers/fileupload";


const requiredMessages = {
  name: "Please enter name",
  dob: "Please select date of birth",
  mobileNo: "Please enter mobile number",
  address: "Please enter address",
  gender: "Please select gender",
  experience: "Please enter experience",
  education: "Please enter education",
  aadhaarNumber: "Please enter aadhaar number",
  religion: "Please enter religion",
  nationality: "Please enter nationality",
  doj: "Please select date of joining",
  dept: "Please select department",
};

export default function EditStaff() {
  const { id } = useParams();
  let user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [imagePreview, setImagePreview] = useState("");

  const staffState = useSelector((state) => state.staff || {});
  const { loading, error } = staffState;

  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    email: "",
    mobileNo: "",
    address: "",
    gender: "",
    experience: "",
    education: "",
    aadhaarNumber: "",
    religion: "",
    nationality: "",
    doj: "",
    dept: "",
    image: null,
  });

  const [errors, setErrors] = useState({});

  //  fetch staff
  useEffect(() => {
    if (id) {
      const item = { id };
      dispatch(getStaff(item))
        .unwrap()
        .then((res) => {
          const matchedStaff = res?.data[0];

          if (matchedStaff) {
            setFormData({
              name: matchedStaff.name || "",
              dob: matchedStaff.dob ? matchedStaff.dob.split("T")[0] : "",
              email: matchedStaff.email || "",
              mobileNo: matchedStaff.mobileNo || "",
              address: matchedStaff.address || "",
              gender: matchedStaff.gender || "",
              experience: matchedStaff.experience || "",
              education: matchedStaff.education || "",
              aadhaarNumber: matchedStaff.aadhaarNumber || "",
              religion: matchedStaff.religion || "",
              nationality: matchedStaff.nationality || "",
              doj: matchedStaff.doj ? matchedStaff.doj.split("T")[0] : "",
              dept: matchedStaff.dept || "",
              image: matchedStaff.image || "",
            });
            // THIS IS MISSING
            setImagePreview(matchedStaff.image || "");
          }
        })
        .catch((err) => console.error(err));
    }
  }, [id, dispatch]);

  //  field validation
  const validateField = (name, value) => {
    let error = "";

    if (!value) {
      error = requiredMessages[name];
    } else {
      if (name === "name" && !nameregex.test(value))
        error = "Name should contain only alphabets and spaces";
      else if (name === "dob" && !isValidDateOfBirth(value))
        error = "Age must be at least 18 years";
      else if (name === "email" && !emailregex.test(value))
        error = "Enter a valid email address";
      else if (name === "mobileNo" && !mobileregex.test(value))
        error = "Mobile number must be 10 digits";
      else if (name === "experience" && !expregex.test(value))
        error = "Experience must be between 1 and 40 years";
      else if (name === "education" && !educationRegex.test(value))
        error = "Enter valid education details";
      else if (name === "aadhaarNumber" && !aadharregex.test(value))
        error = "Aadhaar number must be 12 digits";
      else if (name === "religion" && !religionnameregex.test(value))
        error = "Enter valid religion";
      else if (name === "nationality" && !nationalitynameregex.test(value))
        error = "Enter valid nationality";
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // Allow only digits
    if (["mobileNo", "experience", "aadhaarNumber"].includes(name)) {
      updatedValue = value.replace(/\D/g, "");
    }

    // Mobile: max 10 digits
    if (name === "mobileNo") {
      updatedValue = updatedValue.slice(0, 10);
    }

    // Aadhaar: max 12 digits
    if (name === "aadhaarNumber") {
      updatedValue = updatedValue.slice(0, 12);
    }

    // Experience: max 2 digits
    if (name === "experience") {
      updatedValue = updatedValue.slice(0, 2);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    validateField(name, updatedValue);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate image type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
      e.target.value = "";
      return;
    }

    // Validate size (5 MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must not exceed 5 MB.");
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      image: file,
    }));

    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: "",
    }));

    setImagePreview("");

    const fileInput = document.getElementById("staffImage");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  //  form validation
  const validate = () => {
    const newErrors = {};

    Object.keys(requiredMessages).forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = requiredMessages[field];
      }
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      let imageUrl;

      // upload ONLY if a new file is selected
      if (formData.image && formData.image instanceof File) {
        imageUrl = await uploadadminImage(formData.image, token);
      }

      // 🔥 build CLEAN payload (NO File object)
      const payload = {
        id,
        name: formData.name,
        dob: formData.dob,
        email: formData.email,
        mobileNo: formData.mobileNo,
        address: formData.address,
        gender: formData.gender,
        experience: formData.experience,
        education: formData.education,
        aadhaarNumber: formData.aadhaarNumber,
        religion: formData.religion,
        nationality: formData.nationality,
        doj: formData.doj,
        dept: formData.dept,
        image: imageUrl || imagePreview,
      };

      await dispatch(updateStaff(payload)).unwrap();

      toast.success("Updated successfully");
      navigate("/institute/staffs");
    } catch (err) {
      toast.error(err?.message || "Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }


  if (error) {
    return <p className="text-danger text-center">{error}</p>;
  }

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/institute/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/institute/staffs">Staffs</Link>

            <FiChevronRight className="separator" />

            <span className="active">Edit Staff</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <form className="form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleChange} maxLength={50}
            />
            {errors.name && <small className="error-text">{errors.name}</small>}
          </div>
          <div className="form-group">
            <label>Department</label>
            <select
              name="dept"
              value={formData.dept}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option>Teaching</option>
              <option>Non Teaching</option>
              <option>Accounts</option>
              <option>PT</option>
            </select>
            {errors.dept && (
              <small dept="error-text">{errors.dept}</small>
            )}
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleChange} maxLength={75}
            />
            {errors.email && <small className="error-text">{errors.email}</small>}
          </div>


          <div className="form-group">
            <label>Mobile Number</label>
            <input
              type="text"
              name="mobileNo"
              placeholder="Enter mobile number"
              value={formData.mobileNo}
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={handleChange}
              onBlur={handleChange}
              onKeyDown={(e) =>
                ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
              }
            />
            {errors.mobileNo && (
              <small className="error-text">{errors.mobileNo}</small>
            )}
          </div>


          <div className="form-group">
            <label>Gender</label>
            <select name="gender" value={formData.gender} onChange={handleChange}>
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
            {errors.gender && (
              <small className="error-text">{errors.gender}</small>
            )}
          </div>


          <div className="form-group">
            <label>Education</label>
            <input
              name="education"
              placeholder="Qualification"
              value={formData.education}
              onChange={handleChange}
              onBlur={handleChange} maxLength={20}
            />

            {errors.education && (
              <small className="error-text">{errors.education}</small>
            )}
          </div>

          <div className="form-group">
            <label>Experience (Years)</label>
            <input
              type="number"
              name="experience"
              placeholder="e.g. 5"
              value={formData.experience}
              min="0"
              max="40"
              onKeyDown={(e) =>
                ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
              }
              onChange={handleChange}
              onBlur={handleChange}
            />
            {errors.experience && (
              <small className="error-text">{errors.experience}</small>
            )}
          </div>



          <div className="form-group">
            <label>Aadhaar</label>
            <input
              name="aadhaarNumber"
              placeholder="XXXX XXXX XXXX"
              value={formData.aadhaarNumber}
              onChange={handleChange}
              onBlur={handleChange}
            />
            {errors.aadhaarNumber && (
              <small className="error-text">{errors.aadhaarNumber}</small>
            )}
          </div>

          <div className="form-group">
            <label>Religion</label>
            <input
              name="religion"
              value={formData.religion}
              onChange={handleChange}
              onBlur={handleChange} maxLength={20}
            />
            {errors.religion && (
              <small className="error-text">{errors.religion}</small>
            )}
          </div>

          <div className="form-group">
            <label>Nationality</label>
            <input
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              onBlur={handleChange} maxLength={20}
            />
            {errors.nationality && (
              <small className="error-text">{errors.nationality}</small>
            )}
          </div>

          <div className="form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              onBlur={handleChange}
            />
            {errors.dob && <small className="error-text">{errors.dob}</small>}
          </div>



          <div className="form-group">
            <label>Date of Joining</label>
            <input
              type="date"
              name="doj"
              value={formData.doj}
              onChange={handleChange}
              onBlur={handleChange}
            />
            {errors.doj && <small className="error-text">{errors.doj}</small>}
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea
              rows="3"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
            {errors.address && (
              <small className="error-text">{errors.address}</small>
            )}
          </div>


          <div className="form-group">
            <label>Image</label>
            <input type="file" name="image" onChange={handleFileChange} />
            {imagePreview && (
              <div className="image-preview-wrapper">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="image-preview"
                />

                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={removeImage}
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Update
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
